import {memo, useRef, useCallback, useEffect} from 'react'
import {Conversation} from '@/features/conversations/types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from 'react-i18next'
import {useConversations} from '@/features/conversations/query/useConversations'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {ConversationItem} from '../conversation-item/ConversationItem'
import {ConversationListSkeleton} from '../conversation-list-skeleton/ConversationListSkeleton'
import {StyledConversationsList, StyledEmptyState} from './style'
import {Text} from '@/components/ui/text/Text'
import {HttpGetConversationsOptions} from '../../http/conversations.http'

interface ConversationsListProps {
    patientId: string
    onSelectConversation: (conversation: Conversation) => void
    selectedConversationId?: string
    search?: string
    entityType?: HttpGetConversationsOptions['params']['entityType'] | null
    onDeleteConversation?: (conversationId: string) => void
}

const ConversationsListComponent = ({
    patientId,
    onSelectConversation,
    selectedConversationId,
    search,
    entityType,
    onDeleteConversation
}: ConversationsListProps) => {
    const {t} = useTranslation()
    const endOfListRef = useRef<HTMLDivElement>(null)

    const {conversations, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage} = useConversations(
        patientId,
        30,
        search,
        entityType || undefined
    )

    // Handle intersection observer for infinite scrolling
    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry.isIntersecting && hasNextPage && !isLoading && !isFetchingNextPage) {
                fetchNextPage()
            }
        },
        [hasNextPage, isLoading, isFetchingNextPage, fetchNextPage]
    )

    // Setup intersection observer for infinite scrolling
    useEffect(() => {
        if (!hasNextPage || !endOfListRef.current) return

        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '0px',
            threshold: 0.5
        })

        if (endOfListRef.current) {
            observer.observe(endOfListRef.current)
        }

        return () => {
            if (endOfListRef.current) {
                observer.unobserve(endOfListRef.current)
            }
        }
    }, [handleObserver, hasNextPage, conversations.length])

    if (isLoading && !conversations.length) {
        return <ConversationListSkeleton />
    }

    if (isError) {
        return (
            <StyledEmptyState direction="column" align="center" justify="center" gap={2}>
                <AlertCircleIcon size={24} />
                <Text size="textMd" palette="neutral" shade="600">
                    {t('conversations:error')}
                </Text>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('conversations:error_loading_data')}
                </Text>
            </StyledEmptyState>
        )
    }

    if (!conversations.length) {
        return (
            <StyledEmptyState direction="column" align="center" justify="center" gap={2}>
                <AlertCircleIcon size={24} />
                <Text size="textMd" palette="neutral" shade="600">
                    {t('conversations:no_conversations')}
                </Text>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('conversations:start_new_conversation')}
                </Text>
            </StyledEmptyState>
        )
    }

    return (
        <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
            <StyledConversationsList direction="column" gap={2}>
                {conversations.map(conversation => (
                    <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        isSelected={selectedConversationId === conversation.id}
                        onSelect={onSelectConversation}
                        onDelete={onDeleteConversation}
                    />
                ))}

                {/* Loading indicator for infinite scrolling */}
                {(hasNextPage || isFetchingNextPage) && (
                    <div ref={endOfListRef} style={{height: '20px', padding: '10px 0'}}>
                        {isFetchingNextPage && (
                            <Flexbox justify="center" align="center">
                                <Spinner size={20} />
                            </Flexbox>
                        )}
                    </div>
                )}
            </StyledConversationsList>
        </ScrollArea>
    )
}

export const ConversationsList = memo(ConversationsListComponent)
